import type {
  HistoricalBalancePoint,
  PublicEquity,
} from "../../contracts/src/index.ts";

export type ChartMode = "performance" | "amount";
export type PeriodKey = "1m" | "3m" | "6m" | "1y" | "all";

export interface ChartSeriesPoint {
  recordedAt: number;
  value: number;
  series: "historical" | "liveBalance" | "liveEquity";
  balance?: number;
  equity?: number;
  tradingPnl?: number;
  cashFlow?: number;
}

export interface CashFlowEvent {
  recordedAt: number;
  cashIn: number;
  cashOut: number;
  netCashFlow: number;
  balance: number;
  times: number[];
}

export interface TimeTick {
  recordedAt: number;
  label: string;
  kind: "week" | "month" | "quarter" | "year";
}

export interface EndLabelPosition {
  key: string;
  value: number;
  y: number;
}

const EPSILON = 1e-9;
const DAY_SECONDS = 86_400;

function finite(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function normalizeChartValue(value: number): number {
  // Performance-index ratios can introduce binary floating-point residue
  // (for example 10.000000000000009). Keep enough precision for charting
  // while returning stable percentage values to labels and tests.
  return Number(value.toFixed(12));
}

function monthStart(timestamp: number): number {
  const date = new Date(timestamp * 1000);
  return Math.floor(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1) / 1000);
}

function addUtcMonths(timestamp: number, count: number): number {
  const date = new Date(timestamp * 1000);
  return Math.floor(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + count, 1) / 1000);
}

export function buildAlignedPerformanceSeries(equity: PublicEquity | null): ChartSeriesPoint[] {
  if (!equity) return [];
  const historical = [...equity.historical_balance.points]
    .filter((point) => finite(point.returnPct))
    .sort((left, right) => left.recordedAt - right.recordedAt);
  const live = [...equity.live_equity.points]
    .filter((point) => finite(point.returnPct))
    .sort((left, right) => left.recordedAt - right.recordedAt);
  const liveStart = live.at(0)?.recordedAt ?? Number.POSITIVE_INFINITY;
  const historicalBeforeLive = historical.filter((point) => point.recordedAt < liveStart);
  const historicalAnchor = historicalBeforeLive.length
    ? 1 + historicalBeforeLive.at(-1)!.returnPct / 100
    : 1;
  const liveBase = live.length ? 1 + live[0].returnPct / 100 : 1;
  const liveScale = Math.abs(liveBase) > EPSILON ? historicalAnchor / liveBase : historicalAnchor;
  return [
    ...historicalBeforeLive.map((point) => ({
      recordedAt: point.recordedAt,
      value: point.returnPct,
      series: "historical" as const,
      balance: point.balance,
      tradingPnl: point.tradingPnl,
      cashFlow: point.cashFlow,
    })),
    ...live.map((point) => ({
      recordedAt: point.recordedAt,
      value: normalizeChartValue(((1 + point.returnPct / 100) * liveScale - 1) * 100),
      series: "liveEquity" as const,
      equity: point.equity,
    })),
  ].sort((left, right) => left.recordedAt - right.recordedAt);
}

export function buildAmountSeries(equity: PublicEquity | null): ChartSeriesPoint[] {
  if (!equity) return [];
  return [
    ...equity.historical_balance.points.filter((point) => finite(point.balance)).map((point) => ({
      recordedAt: point.recordedAt,
      value: point.balance,
      balance: point.balance,
      tradingPnl: point.tradingPnl,
      cashFlow: point.cashFlow,
      series: "historical" as const,
    })),
    ...equity.live_balance.points.filter((point) => finite(point.balance)).map((point) => ({
      recordedAt: point.recordedAt,
      value: point.balance,
      balance: point.balance,
      series: "liveBalance" as const,
    })),
    ...equity.live_equity.points.filter((point) => finite(point.equity)).map((point) => ({
      recordedAt: point.recordedAt,
      value: point.equity,
      equity: point.equity,
      series: "liveEquity" as const,
    })),
  ].sort((left, right) => left.recordedAt - right.recordedAt);
}

export function buildCashFlowEvents(
  points: Array<HistoricalBalancePoint | (Omit<HistoricalBalancePoint, "cashFlow"> & { cashFlow: number | null | undefined })>,
): CashFlowEvent[] {
  const byDay = new Map<number, CashFlowEvent>();
  for (const point of points) {
    const hasGrossBreakdown = finite(point.cashIn) || finite(point.cashOut);
    const cashIn = hasGrossBreakdown
      ? Math.max(0, finite(point.cashIn) ? point.cashIn : 0)
      : finite(point.cashFlow) && point.cashFlow > 0 ? point.cashFlow : 0;
    const cashOut = hasGrossBreakdown
      ? Math.max(0, finite(point.cashOut) ? point.cashOut : 0)
      : finite(point.cashFlow) && point.cashFlow < 0 ? Math.abs(point.cashFlow) : 0;
    const netCashFlow = finite(point.cashFlow) ? point.cashFlow : cashIn - cashOut;
    if (cashIn <= EPSILON && cashOut <= EPSILON && Math.abs(netCashFlow) <= EPSILON) continue;
    const day = Math.floor(point.recordedAt / DAY_SECONDS);
    const current = byDay.get(day) ?? {
      recordedAt: point.recordedAt,
      cashIn: 0,
      cashOut: 0,
      netCashFlow: 0,
      balance: point.balance,
      times: [],
    };
    current.cashIn += cashIn;
    current.cashOut += cashOut;
    current.netCashFlow += netCashFlow;
    current.recordedAt = Math.max(current.recordedAt, point.recordedAt);
    current.balance = point.balance;
    current.times.push(...(point.cashFlowTimes?.length ? point.cashFlowTimes : [point.recordedAt]));
    byDay.set(day, current);
  }
  return [...byDay.values()].sort((left, right) => left.recordedAt - right.recordedAt);
}

export function buildAxisTicks(values: number[], count = 5): { min: number; max: number; ticks: number[] } | null {
  const valid = values.filter(finite);
  if (!valid.length) return null;
  const rawMin = Math.min(...valid);
  const rawMax = Math.max(...valid);
  const spread = rawMax - rawMin;
  const padding = Math.max(spread * 0.08, Math.abs(rawMax || rawMin) * 0.05, 1);
  const min = rawMin - padding;
  const max = rawMax + padding;
  const safeCount = Math.min(6, Math.max(4, Math.round(count)));
  return {
    min,
    max,
    ticks: Array.from({ length: safeCount }, (_, index) =>
      min + (max - min) * index / (safeCount - 1)),
  };
}

export function buildTimeTicks(from: number, to: number, period: PeriodKey): TimeTick[] {
  if (!finite(from) || !finite(to) || to < from) return [];
  if (period === "1m") {
    const ticks: TimeTick[] = [];
    const firstDay = Math.floor(from / DAY_SECONDS) * DAY_SECONDS;
    for (let at = firstDay; at <= to; at += 7 * DAY_SECONDS) {
      ticks.push({
        recordedAt: Math.max(at, from),
        label: new Intl.DateTimeFormat("zh-TW", { timeZone: "UTC", month: "2-digit", day: "2-digit" }).format(at * 1000),
        kind: "week",
      });
    }
    return ticks.slice(0, 6);
  }

  const firstMonth = monthStart(from);
  const lastMonth = monthStart(to);
  const months = Math.max(1, Math.round((lastMonth - firstMonth) / (30.4375 * DAY_SECONDS)) + 1);
  const step = period === "all" && months > 60 ? 12 : period === "all" && months > 24 ? 3 : 1;
  const kind: TimeTick["kind"] = step === 12 ? "year" : step === 3 ? "quarter" : "month";
  const ticks: TimeTick[] = [];
  for (let at = firstMonth, index = 0; at <= lastMonth; at = addUtcMonths(firstMonth, ++index * step)) {
    const date = new Date(at * 1000);
    const label = kind === "year"
      ? String(date.getUTCFullYear())
      : kind === "quarter"
        ? `${date.getUTCFullYear()} Q${Math.floor(date.getUTCMonth() / 3) + 1}`
        : `${date.getUTCFullYear()}/${String(date.getUTCMonth() + 1).padStart(2, "0")}`;
    ticks.push({ recordedAt: Math.max(at, from), label, kind });
  }
  return ticks;
}

export function resolveEndLabelPositions(
  labels: Array<{ key: string; value: number }>,
  min: number,
  max: number,
  height: number,
  minimumGap = 28,
): EndLabelPosition[] {
  const spread = max - min || 1;
  const ordered = labels.map((label) => ({
    ...label,
    y: height - (label.value - min) / spread * height,
  })).sort((left, right) => left.y - right.y);
  for (let index = 1; index < ordered.length; index += 1) {
    if (ordered[index].y - ordered[index - 1].y < minimumGap) {
      ordered[index].y = ordered[index - 1].y + minimumGap;
    }
  }
  const overflow = ordered.at(-1) ? Math.max(0, ordered.at(-1)!.y - height) : 0;
  if (overflow > 0) ordered.forEach((label) => { label.y -= overflow; });
  return ordered;
}
